import { useEffect, useState } from 'react';
import { api } from '../api/apiClient';

// Session-level cache: T-ID (upper) -> mitigations[] (missing IDs cached as [])
const mitigationCache = new Map();

const normalizeId = (tech) => {
  const id = tech?.techniqueId || tech?.id;
  const upper = String(id || '').trim().toUpperCase();
  return /^T\d{4}(\.\d{3})?$/.test(upper) ? upper : null;
};

/**
 * useTechniqueMitigations — unified mitigations for classic + scan techniques.
 * Uses embedded `tech.mitigations` when present (scan pool), otherwise batch-fetches
 * missing IDs once per session via POST /api/mitre/techniques/mitigations.
 * @param {Array} techniques - technique objects (any origin)
 * @returns {{ map: Object, loading: boolean }} map: T-ID -> mitigations[]
 */
export const useTechniqueMitigations = (techniques) => {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);

  const signature = JSON.stringify((techniques || []).map((tech) => normalizeId(tech) || tech?.id || null));

  useEffect(() => {
    const embedded = {};
    const missing = [];
    (techniques || []).forEach((tech) => {
      const id = normalizeId(tech);
      if (!id) return;
      if (Array.isArray(tech?.mitigations)) embedded[id] = tech.mitigations;
      else if (mitigationCache.has(id)) embedded[id] = mitigationCache.get(id);
      else if (!missing.includes(id)) missing.push(id);
    });
    setMap(embedded);
    if (missing.length === 0) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.post('/mitre/techniques/mitigations', { ids: missing })
      .then((res) => {
        if (cancelled) return;
        const fetched = res?.mitigations || {};
        missing.forEach((id) => {
          const list = Array.isArray(fetched[id]) ? fetched[id] : [];
          mitigationCache.set(id, list);
        });
        setMap((prev) => {
          const next = { ...prev };
          missing.forEach((id) => { next[id] = mitigationCache.get(id); });
          return next;
        });
      })
      .catch(() => {
        if (cancelled) return;
        // On error, expose empties so sections hide instead of spinning forever
        missing.forEach((id) => { if (!mitigationCache.has(id)) mitigationCache.set(id, []); });
        setMap((prev) => {
          const next = { ...prev };
          missing.forEach((id) => { next[id] = mitigationCache.get(id); });
          return next;
        });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return { map, loading };
};

export default useTechniqueMitigations;
